// src/pages/trading-portal/OptionsContractManagement/OptionsContractManagementDashboard.js
// Options Contract Management dashboard overview

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    AccountBalance as AccountBalanceIcon,
    Notifications as NotificationsIcon,
    AutoMode as AutoModeIcon,
    Search as SearchIcon,
    Assessment as AssessmentIcon,
    EventNote as EventNoteIcon,
} from '@mui/icons-material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// ─── Sub-components ──────────────────────────────────────────────────

const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const StatCardSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '14px' }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1.5rem' }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Navigation items ────────────────────────────────────────────────

const NAV_ITEMS = [
    {
        label: 'Contract Discovery',
        icon: SearchIcon,
        path: '/trading-portal/options-contract-management/contract-discovery',
        color: ACCENT,
        description: 'Search and filter options contracts by symbol, expiration, and strategy',
    },
    {
        label: 'Position Management',
        icon: AssessmentIcon,
        path: '/trading-portal/options-contract-management/positions',
        color: '#8b5cf6',
        description: 'Track open positions, P&L, margin, and roll recommendations',
    },
    {
        label: 'Expiration & Settlement',
        icon: EventNoteIcon,
        path: '/trading-portal/options-contract-management/expiration-settlement',
        color: '#3b82f6',
        description: 'Manage expirations, pin risk, exercise decisions, and auto-close rules',
    },
    {
        label: 'Strategy Analyzer',
        icon: TrendingUpIcon,
        path: '/trading-portal/options-contract-management/strategy-analyzer',
        color: '#10b981',
        description: 'Interactive P&L heatmap and Greeks analysis for option strategies',
    },
];

// ─── Risk-level chip colors ──────────────────────────────────────────

const RISK_LEVEL_STYLES = {
    NONE: { bgcolor: '#f1f5f9', color: '#64748b' },
    LOW: { bgcolor: '#dbeafe', color: '#3b82f6' },
    MODERATE: { bgcolor: '#fef3c7', color: '#d97706' },
    HIGH: { bgcolor: '#fee2e2', color: '#ef4444' },
    CRITICAL: { bgcolor: '#fecaca', color: '#b91c1c' },
};

// ─── Main component ──────────────────────────────────────────────────

const OptionsContractManagementDashboard = () => {
    const navigate = useNavigate();

    const [canView, setCanView] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState(null);

    // Permission check
    useEffect(() => {
        const checkPerms = async () => {
            const view = await hasPermission('tr-ocm-dashboard', 'view');
            setCanView(view);
        };
        checkPerms();
    }, []);

    // Fetch dashboard data
    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axiosInstance.get('/api/options-contract-management/dashboard');
            const data = response.data?.data || response.data || {};
            setDashboardData({
                open_positions: data.open_positions || 0,
                unrealized_pnl: data.unrealized_pnl ?? 0,
                pending_alerts: data.pending_alerts || 0,
                active_auto_close_rules: data.active_auto_close_rules || 0,
                expiring_soon: data.expiring_soon || [],
                recent_pin_risk_alerts: data.recent_pin_risk_alerts || [],
            });
        } catch (err) {
            console.error('Failed to fetch options contract management dashboard:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) {
            fetchDashboard();
        }
    }, [canView, fetchDashboard]);

    // ── Helpers ──────────────────────────────────────────────────────

    const formatCurrency = (value) => {
        if (value == null) return '--';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    // ── Permission gate ──────────────────────────────────────────────

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily }}>
                    You do not have permission to view the Options Contract Management Dashboard.
                </Alert>
            </Box>
        );
    }

    // ── Render ───────────────────────────────────────────────────────

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ── Gradient Header ───────────────────────────────────── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)',
                    borderRadius: 3,
                    p: 4,
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -40,
                        right: -40,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: `${ACCENT}15`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        left: '30%',
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: `${ACCENT}10`,
                    }}
                />
                <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: '#fff', fontFamily, position: 'relative', zIndex: 1 }}
                >
                    Options Contract Management
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: 'rgba(255,255,255,0.7)', fontFamily, mt: 1, position: 'relative', zIndex: 1 }}
                >
                    Contract discovery, position management, and expiration settlement
                </Typography>
            </Box>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* ── Error Alert ──────────────────────────────────── */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={fetchDashboard}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* ── Stat Cards ──────────────────────────────────── */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    {/* Open Positions */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={TrendingUpIcon}
                                title="Open Positions"
                                value={dashboardData?.open_positions ?? 0}
                                color="#22c55e"
                                subtitle="Currently active"
                            />
                        )}
                    </Grid>

                    {/* Unrealized P&L */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={AccountBalanceIcon}
                                title="Unrealized P&L"
                                value={
                                    <Typography
                                        component="span"
                                        variant="h5"
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            color: (dashboardData?.unrealized_pnl ?? 0) >= 0 ? '#22c55e' : '#ef4444',
                                        }}
                                    >
                                        {formatCurrency(dashboardData?.unrealized_pnl ?? 0)}
                                    </Typography>
                                }
                                color={(dashboardData?.unrealized_pnl ?? 0) >= 0 ? '#22c55e' : '#ef4444'}
                                subtitle="Across all open positions"
                            />
                        )}
                    </Grid>

                    {/* Pending Alerts */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={NotificationsIcon}
                                title="Pending Alerts"
                                value={dashboardData?.pending_alerts ?? 0}
                                color="#f97316"
                                subtitle="Requires attention"
                            />
                        )}
                    </Grid>

                    {/* Active Auto-Close Rules */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={AutoModeIcon}
                                title="Active Auto-Close Rules"
                                value={dashboardData?.active_auto_close_rules ?? 0}
                                color="#3b82f6"
                                subtitle="Automated protection"
                            />
                        )}
                    </Grid>
                </Grid>

                {/* ── Two-Column Layout: Expiring Soon + Pin Risk Alerts ── */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* Expiring Soon */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EventNoteIcon sx={{ color: '#f97316', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Expiring Soon
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/options-contract-management/expiration-settlement')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 1, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 1.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : !dashboardData?.expiring_soon?.length ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <EventNoteIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No positions expiring within 7 days
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Expiring positions will appear here when detected
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Symbol</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Strategy</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Expiration</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }} align="right">Days</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }} align="right">P&L</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dashboardData.expiring_soon.slice(0, 5).map((pos, index) => (
                                                        <TableRow key={pos.id || index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {pos.symbol || '--'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {pos.strategy || '--'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {pos.nearest_expiration
                                                                    ? new Date(pos.nearest_expiration).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                                                    : '--'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <Chip
                                                                    label={`${pos.days_to_nearest_expiry ?? '--'}d`}
                                                                    size="small"
                                                                    sx={{
                                                                        fontFamily,
                                                                        fontWeight: 600,
                                                                        fontSize: '0.7rem',
                                                                        height: 24,
                                                                        bgcolor: (pos.days_to_nearest_expiry ?? 99) <= 1 ? '#fee2e2' : (pos.days_to_nearest_expiry ?? 99) <= 3 ? '#fef3c7' : '#dbeafe',
                                                                        color: (pos.days_to_nearest_expiry ?? 99) <= 1 ? '#ef4444' : (pos.days_to_nearest_expiry ?? 99) <= 3 ? '#d97706' : '#3b82f6',
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={{
                                                                    fontFamily,
                                                                    fontWeight: 600,
                                                                    fontSize: '0.82rem',
                                                                    color: (pos.unrealized_pnl ?? 0) >= 0 ? '#22c55e' : '#ef4444',
                                                                    borderBottom: '1px solid #f1f5f9',
                                                                }}
                                                            >
                                                                {formatCurrency(pos.unrealized_pnl)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Pin Risk Alerts */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <NotificationsIcon sx={{ color: '#ef4444', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Recent Pin Risk Alerts
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/options-contract-management/expiration-settlement')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 2, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 0.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : !dashboardData?.recent_pin_risk_alerts?.length ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <NotificationsIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No recent pin risk alerts
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Pin risk alerts will appear here when underlying prices approach short strikes
                                            </Typography>
                                        </Box>
                                    ) : (
                                        dashboardData.recent_pin_risk_alerts.slice(0, 5).map((alert, index) => {
                                            const riskStyle = RISK_LEVEL_STYLES[alert.risk_level] || RISK_LEVEL_STYLES.NONE;
                                            return (
                                                <Box
                                                    key={alert.id || index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        py: 1.5,
                                                        px: 0.5,
                                                        borderBottom: '1px solid #f1f5f9',
                                                        '&:last-child': { borderBottom: 'none' },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 42,
                                                            height: 42,
                                                            borderRadius: '10px',
                                                            bgcolor: riskStyle.bgcolor,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <NotificationsIcon sx={{ fontSize: 22, color: riskStyle.color }} />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                                            {alert.symbol || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                                            Strike: {alert.short_strike != null ? formatCurrency(alert.short_strike) : '--'}
                                                            {' | '}
                                                            Underlying: {alert.underlying_price != null ? formatCurrency(alert.underlying_price) : '--'}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={alert.risk_level || 'Unknown'}
                                                        size="small"
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            height: 24,
                                                            bgcolor: riskStyle.bgcolor,
                                                            color: riskStyle.color,
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Navigation Cards ────────────────────────────────── */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 2 }}>
                        Sub-Modules
                    </Typography>
                    <Grid container spacing={2}>
                        {NAV_ITEMS.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.path}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: item.color,
                                            boxShadow: `0 4px 20px ${item.color}20`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                    onClick={() => navigate(item.path)}
                                >
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '14px',
                                                        bgcolor: `${item.color}15`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <item.icon sx={{ color: item.color, fontSize: 24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.78rem', fontWeight: 500 }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <NavigateNextIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};

export default OptionsContractManagementDashboard;
